import { useMemo, useState } from 'react';
import { animeList, genreOptions } from '@/mocks/anime';

const PAGE_SIZE = 8;

export default function BrowseSection() {
  const [keyword, setKeyword] = useState('');
  const [activeGenre, setActiveGenre] = useState('全部');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return animeList.filter((item) => {
      const matchGenre = activeGenre === '全部' || item.genre === activeGenre;
      const matchKeyword =
        kw === '' ||
        item.title.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw)) ||
        item.summary.toLowerCase().includes(kw);
      return matchGenre && matchKeyword;
    });
  }, [keyword, activeGenre]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const handleGenre = (genre: string) => {
    setActiveGenre(genre);
    setVisibleCount(PAGE_SIZE);
  };

  return (
    <section id="browse" className="bg-background-50 py-14 md:py-20">
      <div className="w-full px-4 md:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
          <div className="max-w-2xl">
            <span className="text-xs font-medium tracking-wide text-primary-600">作品浏览</span>
            <h2 className="mt-2 font-heading text-2xl md:text-4xl font-semibold text-foreground-950">
              按题材与关键词找到想看的作品
            </h2>
            <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-700">
              下面列出的都是本页已收录的作品，标签与搜索只会筛选这里已有的内容。先缩小范围，再看更新状态与简介，决定从哪一部开始。
            </p>
          </div>

          <div className="w-full lg:w-80">
            <label htmlFor="anime-search" className="block text-xs text-foreground-500 mb-2">
              搜索作品标题或标签
            </label>
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2 focus-within:border-primary-400">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm"></i>
              </span>
              <input
                id="anime-search"
                type="text"
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setVisibleCount(PAGE_SIZE);
                }}
                placeholder="例如：星海、悬疑、治愈"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
              {keyword && (
                <button
                  type="button"
                  aria-label="清空搜索"
                  onClick={() => {
                    setKeyword('');
                    setVisibleCount(PAGE_SIZE);
                  }}
                  className="w-5 h-5 flex items-center justify-center text-foreground-400 transition-colors hover:text-primary-600"
                >
                  <i className="ri-close-circle-line text-sm"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-7 flex flex-wrap gap-2">
          {genreOptions.map((genre) => (
            <button
              key={genre}
              type="button"
              onClick={() => handleGenre(genre)}
              className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                activeGenre === genre
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-700'
              }`}
            >
              {genre}
            </button>
          ))}
        </div>

        <p className="mt-5 text-sm text-foreground-500">
          共找到 <strong className="text-foreground-900 font-semibold">{filtered.length}</strong> 部作品
          {activeGenre !== '全部' && <> · 题材「{activeGenre}」</>}
          {keyword.trim() && <> · 关键词「{keyword.trim()}」</>}
        </p>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {filtered.map((item, index) => (
              <article
                key={item.id}
                className={`group rounded-lg overflow-hidden bg-background-50 border border-background-200 transition-colors hover:border-primary-300 ${
                  index >= visibleCount ? 'hidden' : ''
                }`}
              >
                <div className="relative w-full aspect-[3/4] overflow-hidden bg-background-100">
                  <img
                    src={item.image}
                    alt={`${item.title} 作品海报`}
                    title={`${item.title} ${item.genre}动画`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-medium bg-background-50/95 text-primary-700">
                    {item.genre}
                  </span>
                  <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-medium bg-foreground-950/75 text-background-50">
                    {item.status}
                  </span>
                </div>

                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-base font-semibold text-foreground-950 leading-snug">
                      {item.title}
                    </h3>
                    <span className="shrink-0 flex items-center gap-1 text-sm font-semibold text-accent-600">
                      <i className="ri-star-fill text-xs"></i>
                      {item.rating.toFixed(1)}
                    </span>
                  </div>

                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {item.tags.map((tag) => (
                      <span key={tag} className="px-2 py-0.5 rounded text-xs bg-secondary-100 text-secondary-900">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <p className="mt-3 text-xs leading-relaxed text-foreground-600 line-clamp-3">
                    {item.summary}
                  </p>

                  <div className="mt-3.5 pt-3 border-t border-background-200 flex items-center justify-between text-xs text-foreground-500">
                    <span>{item.year} 年</span>
                    <span>
                      共 <strong className="text-foreground-800 font-semibold">{item.episodes}</strong> 集
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
            <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-50 text-foreground-400">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <p className="mt-4 text-sm text-foreground-700">
              没有找到与当前条件匹配的作品，试试更换题材或关键词。
            </p>
            <button
              type="button"
              onClick={() => {
                setKeyword('');
                setActiveGenre('全部');
                setVisibleCount(PAGE_SIZE);
              }}
              className="mt-4 inline-flex items-center gap-1 px-4 py-2 rounded-md bg-secondary-500 text-background-50 text-sm whitespace-nowrap transition-colors hover:bg-secondary-600"
            >
              <i className="ri-refresh-line"></i>
              重置筛选条件
            </button>
          </div>
        )}

        {hasMore && (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 whitespace-nowrap transition-colors hover:bg-background-100"
            >
              <i className="ri-add-line"></i>
              加载更多作品
            </button>
          </div>
        )}
      </div>
    </section>
  );
}